import { Button, DatePicker, Form, Modal, Select, TreeSelect } from '@maxtropy/components';
import React, { FC, useEffect, useState } from 'react';
import styles from './index.module.scss';
import {
  apiV2ProductionCostGetEnergyUnitGroupListPost,
  apiV2ProductionCostGetGroupEnergyUnitListPost,
  apiV2ProductionCostRecalculatePost,
} from '@maxtropy/device-customer-apis-v2';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';

const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

export interface IReCalcModal {
  onCancel?: () => void;
  openModal: boolean;
}

const ReCalcModal: FC<IReCalcModal> = props => {
  const { openModal, onCancel } = props;
  const [form] = Form.useForm();
  const [value, setValue] = useState<RangeValue>(null);
  const [dates, setDates] = useState<RangeValue>();
  const energyUnitGroupId: number = Form.useWatch('energyUnitGroupId', form); // 用能分析组id

  // 获取用能分析组
  const { data: groupOptions } = useRequest(() =>
    apiV2ProductionCostGetEnergyUnitGroupListPost({}).then(res => {
      return (res.list ?? []).map(item => ({ label: item.name as string, value: item.id as number }));
    })
  );

  // 获取用能单元
  const { data: unitTreeData } = useRequest(
    () =>
      apiV2ProductionCostGetGroupEnergyUnitListPost({
        energyUnitGroupId: String(energyUnitGroupId),
      }).then(res => {
        return res.list ?? [];
      }),
    {
      refreshDeps: [energyUnitGroupId],
      ready: !!energyUnitGroupId,
    }
  );

  const onFinish = () => {
    form
      .validateFields()
      .then(values => {
        const { date, energyUnitIds, energyUnitGroupId } = values;
        apiV2ProductionCostRecalculatePost({
          energyUnitGroupId: energyUnitGroupId,
          energyUnitIds: energyUnitIds.map((i: { value: any }) => i.value),
          startTime: dayjs(date[0]).valueOf(),
          endTime: dayjs(date[1]).valueOf(),
        }).then((_: any) => {
          onCancel?.();
        });
      })
      .catch(err => {});
  };

  const disabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months') >= 6;
    const tooEarly = dates[1] && dates[1].diff(current, 'months') >= 6;
    const afterToday = current && current > dayjs().endOf('day');
    return !!tooEarly || !!tooLate || !!afterToday;
  };
  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  return (
    <Modal
      title="重计算"
      forceRender
      className={styles.calcModalBox}
      open={openModal}
      afterClose={() => form.resetFields()}
      onCancel={onCancel}
      contentClassName="modal-form-content"
      footer={[
        <Button key="back" onClick={onCancel} className={styles.cancelBtn}>
          取消
        </Button>,
        <Button key="submit" type="primary" onClick={onFinish}>
          确定
        </Button>,
      ]}
    >
      <div className={styles.modalBody}>
        <div className={styles.tipWrapper}>
          <ExclamationCircleOutlined style={{ color: 'var(--mx-warning-color)', fontSize: 12 }} />
          <span className={styles.txt}>
            采集用能数据异常、历史指标配置发生变更会影响数据准确性，需要重计算。重计算大约需要5-30分钟，数据依然异常可联系系统管理员排查。
          </span>
        </div>

        <Form form={form}>
          <Form.Item label="选择时间" name="date" rules={[{ required: true, message: '请选择时间' }]}>
            <RangePicker
              value={value || dates}
              disabledDate={disabledDate}
              onCalendarChange={val => {
                setDates(val);
              }}
              onChange={val => {
                setValue(val);
              }}
              onOpenChange={onOpenChange}
            />
          </Form.Item>
          <Form.Item label="用能分析组" name="energyUnitGroupId" required>
            <Select
              placeholder="请选择"
              options={groupOptions}
              onChange={_ => {
                form.setFieldValue('energyUnitIds', []);
              }}
            />
          </Form.Item>
          <Form.Item label="用能单元" name="energyUnitIds" rules={[{ required: true, message: '请选择用能单元' }]}>
            <TreeSelect
              popupClassName={styles.treeSelect}
              treeData={unitTreeData}
              fieldNames={{ label: 'name', value: 'energyUnitId', children: 'children' }}
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_ALL}
              treeCheckStrictly={true}
              placeholder="请选择"
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default ReCalcModal;
